import React, { useState } from 'react';
import { Tabs, Tab, Box, ToggleButtonGroup, ToggleButton, TextField, Button, Stack, MenuItem, Typography, Alert, CircularProgress, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Avatar } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import ImageIcon from '@mui/icons-material/Image';
import CallIcon from '@mui/icons-material/Call';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SmsAddressBookPanel from './SmsAddressBookPanel';
import EditIcon from '@mui/icons-material/Edit';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../firebaseConfig';

type TabPanelProps = { index: number; value: number; children?: React.ReactNode };
const TabPanel: React.FC<TabPanelProps> = ({ index, value, children }) => {
  return (
    <div role="tabpanel" hidden={value !== index} style={{ height: '100%' }}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
};

const SmsPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  return (
    <div style={{ padding: 12, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="sms tabs" sx={{ ml: 'auto' }}>
          <Tab label="문자 보내기" sx={{ fontSize: 15 }} />
          <Tab label="템플릿" sx={{ fontSize: 15 }} />
        </Tabs>
      </Box>

      <TabPanel value={tab} index={0}>
        <SendTabSkeleton />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <TemplateTab />
      </TabPanel>
      
    </div>
  );
};

export default SmsPage;

// 문자 보내기 탭 스켈레톤(단건/대량 토글 + 기본 폼)
const SendTabSkeleton: React.FC = () => {
  const [mode, setMode] = useState<'single' | 'bulk'>('single');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [companies, setCompanies] = useState<Array<{id:string; name:string}>>([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [templateOptions, setTemplateOptions] = useState<Array<{id:string; name:string}>>([]);
  const [bulkPhones, setBulkPhones] = useState(''); // 줄바꿈 구분
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedMedia, setSelectedMedia] = useState<TemplateMedia[]>([]);
  const [mmsSending, setMmsSending] = useState(false);
  const [monthlyCount, setMonthlyCount] = useState<number | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState<number>(-1);
  React.useEffect(() => {
    (async () => {
      try {
        const d = new Date();
        d.setDate(1);
        d.setHours(0, 0, 0, 0);
        const from = d.getTime();
        const to = Date.now();
        const res = await fetch(`/api/sms/stats?from=${from}&to=${to}`);
        const text = await res.text();
        let data: any = {}; try { data = JSON.parse(text); } catch { data = {}; }
        if (res.ok) setMonthlyCount(Number(data?.totals?.total || 0)); else setMonthlyCount(0);
      } catch { setMonthlyCount(0); }
    })();
  }, []);

  // 전송 완료 후 입력 초기화
  const resetForm = () => {
    setPhone('');
    setMessage('');
    setSelectedCompany('');
    setTemplateId('');
    setSelectedMedia([]);
    setBulkPhones('');
    setAlert(null);
  };

  const splitPhones = (raw: string): string[] => {
    return raw
      .split(/\r?\n|[;,\s]+/)
      .map(s => s.trim())
      .filter(Boolean);
  };

  const validate = (p: string) => /^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/.test(p);

  // 템플릿이 존재하는 회사 목록 로드
  React.useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/sms/templates/companies');
        const raw = await res.text();
        let data: any = {}; try { data = JSON.parse(raw); } catch { return; }
        if (!res.ok || data?.success === false) return;
        setCompanies(data.companies || []);
      } catch {}
    })();
  }, []);

  // 회사 선택 시 템플릿 목록 로드
  React.useEffect(() => {
    (async () => {
      if (!selectedCompany) { setTemplateOptions([]); return; }
      try {
        const res = await fetch(`/api/sms/templates?companyId=${encodeURIComponent(selectedCompany)}`);
        const raw = await res.text();
        let data: any = {}; try { data = JSON.parse(raw); } catch { return; }
        if (!res.ok || data?.success === false) return;
        const opts = (data.templates || []).map((t: any) => ({ id: t.id, name: t.name || '(제목 없음)' }));
        setTemplateOptions(opts);
      } catch {}
    })();
  }, [selectedCompany]);

  const handleSendSingle = async () => {
    setAlert(null);
    if (!phone || !message.trim()) {
      setAlert({ type: 'error', text: '수신번호와 메시지를 입력해주세요.' });
      return;
    }
    if (!validate(phone)) {
      setAlert({ type: 'error', text: '올바른 휴대폰 번호 형식이 아닙니다.' });
      return;
    }
    try {
      setLoading(true);
      const res = await fetch('/api/sms/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: phone, content: message, media: selectedMedia.map(m => m.url) })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || '전송 실패');
      setAlert({ type: 'success', text: '문자 전송이 완료되었습니다.' });
      setTimeout(() => { setAlert(null); resetForm(); }, 1000);
    } catch (e: any) {
      setAlert({ type: 'error', text: e.message || '전송 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSendBulk = async () => {
    setAlert(null);
    const list = splitPhones(bulkPhones);
    if (list.length === 0 || !message.trim()) {
      setAlert({ type: 'error', text: '수신번호 목록과 메시지를 입력해주세요.' });
      return;
    }
    const invalid = list.filter(p => !validate(p));
    if (invalid.length > 0) {
      setAlert({ type: 'error', text: `형식이 올바르지 않은 번호: ${invalid.slice(0,3).join(', ')}${invalid.length>3?' 등':''}` });
      return;
    }
    try {
      setLoading(true);
      const messages = list.map(p => ({ to: p, content: message }));
      const res = await fetch('/api/sms/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || '대량 전송 실패');
      setAlert({ type: 'success', text: `대량 전송이 접수되었습니다. 총 ${messages.length}건` });
      setTimeout(() => { setAlert(null); resetForm(); }, 1000);
    } catch (e: any) {
      setAlert({ type: 'error', text: e.message || '대량 전송 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSendBulkMms = async () => {
    setAlert(null);
    const list = splitPhones(bulkPhones);
    if (list.length === 0 || !message.trim()) {
      setAlert({ type: 'error', text: '수신번호 목록과 메시지를 입력해주세요.' });
      return;
    }
    const invalid = list.filter(p => !validate(p));
    if (invalid.length > 0) {
      setAlert({ type: 'error', text: `형식이 올바르지 않은 번호: ${invalid.slice(0,3).join(', ')}${invalid.length>3?' 등':''}` });
      return;
    }
    if (selectedMedia.length === 0) {
      setAlert({ type: 'error', text: '이미지가 없습니다. 이미지 첨부 후 다시 시도하세요.' });
      return;
    }
    try {
      setMmsSending(true);
      // 이미지 업로드(한 번만)
      const fileIds: string[] = [];
      for (const m of selectedMedia) {
        const uRes = await fetch('/api/sms/mms/upload', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ fileUrl: m.url }) });
        const uRaw = await uRes.text();
        let u: any = {}; try { u = JSON.parse(uRaw); } catch { continue; }
        if (u?.success && u.fileId) fileIds.push(u.fileId);
      }
      if (fileIds.length === 0) { setAlert({ type: 'error', text: 'MMS 파일 업로드 실패' }); setMmsSending(false); return; }

      // 순차 발송(성공/실패 카운트)
      let success = 0; let failed = 0;
      for (const to of list) {
        const sRes = await fetch('/api/sms/mms/send', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ to, content: message || ' ', subject: '', fileIds }) });
        if (sRes.ok) success++; else failed++;
      }
      setAlert({ type: failed === 0 ? 'success' : 'error', text: `대량 MMS 발송 완료: 성공 ${success}건${failed?`, 실패 ${failed}건`:''}` });
      setTimeout(() => { setAlert(null); resetForm(); }, 1000);
    } catch (e: any) {
      setAlert({ type: 'error', text: e?.message || '대량 MMS 발송 실패' });
    } finally {
      setMmsSending(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ alignItems: 'flex-start', width: '100%' }}>
      {/* 상단: 모드 전환(좌) + 초기화(우) */}
      <Box sx={{ width: { xs: 360, sm: 440 }, display: 'flex', alignItems: 'center' }}>
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)}>
          <ToggleButton value="single">단건</ToggleButton>
          <ToggleButton value="bulk">대량</ToggleButton>
        </ToggleButtonGroup>
        <Button size="small" variant="outlined" onClick={resetForm} sx={{ ml: 'auto' }}>초기화</Button>
      </Box>

      {alert && <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ width: 360 }}>{alert.text}</Alert>}

      {/* 이번달 총 발송 건수 */}
      <Box sx={{ display:'flex', alignItems:'center', gap:1, color:'#555' }}>
        <Typography sx={{ fontWeight:700 }}>이번달 총 발송:</Typography>
        <Typography>{monthlyCount == null ? '불러오는 중...' : `${monthlyCount}건`}</Typography>
      </Box>

      {/* 본문: 좌(문자 카드) + 우(주소록 패널) */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {/* 휴대폰 모양 카드 */}
        <Box sx={{ width: { xs: 360, sm: 440 }, border: '1px solid #e0e0e0', borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
        {/* 헤더 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}><CallIcon /></Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 12, opacity: 0.9 }}>수신자</Typography>
            <TextField
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={mode==='bulk' ? '대량 발송은 수신번호 목록에 입력하세요' : '010-1234-5678'}
              size="small"
              disabled={mode==='bulk'}
              sx={{ bgcolor: '#fff', borderRadius: 1, width: '100%' }}
            />
          </Box>
        </Box>

        {/* 본문 */}
        <Box sx={{ p: 2, background: 'linear-gradient(180deg, #eef1f8 0%, #e1e7ff 100%)' }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
            <TextField select label="거래처" value={selectedCompany} onChange={(e)=>setSelectedCompany(e.target.value)} size="small" sx={{ minWidth: 160, flexGrow: 1 }}>
              <MenuItem value="">선택</MenuItem>
              {companies.map(c => (<MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>))}
            </TextField>
            <TextField select label="템플릿" value={templateId} onChange={(e) => setTemplateId(e.target.value)} size="small" sx={{ minWidth: 180, flexGrow: 1 }}>
              <MenuItem value="">선택 안 함</MenuItem>
              {templateOptions.map(function(t){ return (<MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>); })}
            </TextField>
            <Button variant="outlined" size="small" sx={{ flexShrink: 0 }} onClick={async function(){
              if (!selectedCompany || !templateId) return;
              try {
                const res = await fetch(`/api/sms/templates/${templateId}?companyId=${encodeURIComponent(selectedCompany)}`);
                const raw = await res.text();
                let data: any = {}; try { data = JSON.parse(raw); } catch { return; }
                if (!res.ok || data?.success === false) return;
                const content = data.template?.content || '';
                const media = Array.isArray(data.template?.media) ? data.template.media.filter((m: any)=>m && m.type && String(m.type).startsWith('image')) : [];
                setMessage(content);
                setSelectedMedia(media);
              } catch {}
            }}>템플릿 불러오기</Button>
            <Button variant="outlined" size="small" component="label" sx={{ flexShrink: 0 }}>이미지 첨부
              <input hidden type="file" accept="image/*" multiple onChange={async (e)=>{
                const inputEl = e.currentTarget as HTMLInputElement | null;
                const files = Array.from((inputEl && inputEl.files) ? inputEl.files : []);
                if (files.length === 0) return;
                const uploaded: any[] = [];
                for (const f of files) {
                  try {
                    const formData = new FormData();
                    formData.append('file', f);
                    const upRes = await fetch('/api/sms/templates/upload', { method: 'POST', body: formData });
                    const upRaw = await upRes.text();
                    let up: any = {}; try { up = JSON.parse(upRaw); } catch { continue; }
                    if (!upRes.ok || up?.success === false) continue;
                    uploaded.push({ type: 'image', url: up.publicUrl, name: up.name || f.name, size: up.size || f.size });
                  } catch { /* ignore single file error */ }
                }
                if (uploaded.length > 0) setSelectedMedia(prev => [...prev, ...uploaded]);
                // reset input value to allow re-select same files
                if (inputEl) { (inputEl as HTMLInputElement).value = ''; }
              }} />
            </Button>
          </Stack>

          <Box sx={{ border: '1px solid #e6e8f0', borderRadius: 2, p: 1.2, bgcolor: '#ffffff', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>메시지</Typography>
            <TextField
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="메시지 내용을 입력하세요."
              multiline
              minRows={6}
              fullWidth
              helperText={message.length + '/1000자'}
            />
            {/* 템플릿 이미지 미리보기 */}
            {selectedMedia.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                {selectedMedia.map((m, idx) => (
                  <Box
                    key={idx}
                    sx={{ position: 'relative', width: 72, height: 72, cursor: 'pointer' }}
                    onClick={() => { setPreviewIndex(idx); setPreviewOpen(true); }}
                  >
                    <img src={m.url} alt={m.name || 'img'} style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 4, border: '1px solid #e0e0e0' }} />
                    <button
                      aria-label="이미지 삭제"
                      title="삭제"
                      onClick={(e) => {
                        e.stopPropagation();
                        const next = selectedMedia.slice();
                        next.splice(idx, 1);
                        setSelectedMedia(next);
                      }}
                      style={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        background: '#ef4444',
                        color: '#ffffff',
                        border: '2px solid #ffffff',
                        borderRadius: '50%',
                        width: 24,
                        height: 24,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 16,
                        fontWeight: 700,
                        lineHeight: 1,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.35)'
                      }}
                    >
                      ×
                    </button>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        <Divider />
        {/* 하단 전송 버튼 */}
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', gap: 1, background: 'linear-gradient(180deg, #eef1f8 0%, #e1e7ff 100%)', borderTop: '1px solid #e3e6f0', width: { xs: 360, sm: 440 } }}>
          <Button variant="contained" startIcon={<SendIcon />} disabled={loading || selectedMedia.length > 0} onClick={mode==='single'?handleSendSingle:handleSendBulk} title={selectedMedia.length>0 ? '이미지가 첨부된 경우 MMS로 발송을 사용하세요' : undefined}>
            {mode === 'single' ? (loading ? '전송 중...' : '문자 발송') : (loading ? '전송 중...' : '대량 발송')}
          </Button>
          {selectedMedia.length > 0 && mode === 'single' && (
            <Button sx={{ ml: 1 }} variant="outlined" startIcon={<ImageIcon />} disabled={mmsSending} onClick={async ()=>{
              try {
                setMmsSending(true);
                // 파일 업로드 (이미지는 이미 공개 URL이므로 업로드 API로 fileId 확보)
                const fileIds: string[] = [];
                for (const m of selectedMedia) {
                  const uRes = await fetch('/api/sms/mms/upload', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ fileUrl: m.url }) });
                  const uRaw = await uRes.text();
                  let u: any = {}; try { u = JSON.parse(uRaw); } catch { continue; }
                  if (u?.success && u.fileId) fileIds.push(u.fileId);
                }
                if (fileIds.length === 0) { setAlert({ type: 'error', text: 'MMS 파일 업로드 실패' }); setMmsSending(false); return; }
                // MMS 발송
                const sRes = await fetch('/api/sms/mms/send', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ to: phone, content: message || ' ', subject: '', fileIds }) });
                const sRaw = await sRes.text();
                let s: any = {}; try { s = JSON.parse(sRaw); } catch { s = {}; }
                if (!sRes.ok || s?.success === false) { setAlert({ type: 'error', text: s?.error || 'MMS 발송 실패' }); }
                else { setAlert({ type: 'success', text: 'MMS 발송 완료' }); setTimeout(() => { setAlert(null); resetForm(); }, 1000); }
              } catch (e: any) {
                setAlert({ type: 'error', text: e?.message || 'MMS 발송 실패' });
              } finally { setMmsSending(false); }
            }}>
              {mmsSending ? 'MMS 전송 중...' : 'MMS로 발송'}
            </Button>
          )}
          {selectedMedia.length > 0 && mode === 'bulk' && (
            <Button variant="outlined" startIcon={<ImageIcon />} disabled={mmsSending} onClick={handleSendBulkMms}>
              {mmsSending ? '대량 MMS 전송 중...' : '대량 MMS 발송'}
            </Button>
          )}
        </Box>
      </Box>
      {/* 이미지 미리보기 다이얼로그 (문자 보내기 탭) */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: { background: '#000' }
        }}
      >
        <Box sx={{ position: 'relative', p: 0, bgcolor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconButton onClick={() => setPreviewOpen(false)} sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', zIndex: 1 }} size="small">
            <CloseIcon />
          </IconButton>
          {previewIndex >= 0 && selectedMedia[previewIndex] && (
            <img
              src={selectedMedia[previewIndex].url}
              alt={`preview-${previewIndex}`}
              style={{ maxWidth: '90vw', maxHeight: '80vh', width: 'auto', height: 'auto' }}
              onClick={() => setPreviewOpen(false)}
            />
          )}
        </Box>
      </Dialog>

      {/* 주소록/그룹 패널 (우측) */}
      <SmsAddressBookPanel
        mode={mode}
        onPickPhone={(p)=> setPhone(p)}
        onAppendPhones={(phones)=> {
          const merged = (bulkPhones ? bulkPhones.split(/\r?\n|[;,]+/).map(s=>s.trim()).filter(Boolean) : [])
            .concat(phones)
            .map(s=> s.replace(/[^0-9-]/g,''));
          // dedupe preserving order
          const seen = new Set<string>();
          const unique = merged.filter(v => (seen.has(v) ? false : (seen.add(v), true)));
          setBulkPhones(unique.join('\n'));
        }}
      />
      {/* 대량 모드: 수신번호 목록을 오른쪽에 배치 */}
      {mode === 'bulk' && (
        <Box sx={{ width: 320, border: '1px solid #e0e0e0', borderRadius: 2, p: 1 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>수신번호 목록</Typography>
          <TextField
            value={bulkPhones}
            onChange={(e) => setBulkPhones(e.target.value)}
            placeholder={'한 줄에 하나씩 번호입력\n예)\n01012345678\n01022223333\n01033335555'}
            multiline
            minRows={12}
            fullWidth
          />
          {/* CSV 업로드 기능 제거 */}
        </Box>
      )}
      </Box>
    </Stack>
  );
};

// 템플릿 탭 구현
type TemplateMedia = { type: string; url: string; name?: string; size?: number };
type Template = { id?: string; name: string; content: string; isDefault?: boolean; media?: TemplateMedia[] };

// 대시보드 탭
const SmsDashboardTab: React.FC = () => {
  const [range, setRange] = useState<'day'|'week'|'month'>('day');
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const from = React.useMemo(() => {
    const d = new Date();
    if (range === 'day') d.setDate(d.getDate() - 1);
    else if (range === 'week') d.setDate(d.getDate() - 7);
    else d.setMonth(d.getMonth() - 1);
    return d.getTime();
  }, [range]);

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const qs = `from=${from}&to=${Date.now()}`;
        const res = await fetch(`/api/sms/stats?${qs}`);
        const text = await res.text();
        let data: any = {}; try { data = JSON.parse(text); } catch { data = {}; }
        if (res.ok) setStats(data); else setStats(null);
      } catch {
        setStats(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [from]);

  const [daily, setDaily] = useState<Array<{ day: string; total: number; success: number; failed: number }>>([]);
  React.useEffect(() => {
    (async () => {
      try {
        const qs = `from=${from}&to=${Date.now()}`;
        const res = await fetch(`/api/sms/stats/daily?${qs}`);
        const text = await res.text();
        let data: any = {}; try { data = JSON.parse(text); } catch { data = {}; }
        if (res.ok && data?.rows) setDaily(data.rows); else setDaily([]);
      } catch {
        setDaily([]);
      }
    })();
  }, [from]);

  const kpi = stats?.totals || { total:0, success:0, failed:0, pending:0, successRate:0 };
  const types = stats?.types || { sms:0, mms:0, single:0, bulk:0 };
  const [unit, setUnit] = useState<{ sms: number; mms: number }>({ sms: 20, mms: 50 });
  const totalCost = unit.sms * (types.sms || 0) + unit.mms * (types.mms || 0);
  const days = Math.max(1, Math.ceil((Date.now() - from) / (1000*60*60*24)));
  const avgCost = Math.round(totalCost / days);
  const [ops, setOps] = useState<any>(null);
  React.useEffect(()=>{
    (async ()=>{
      try {
        const res = await fetch('/api/sms/stats/ops');
        const text = await res.text();
        let data: any = {}; try { data = JSON.parse(text); } catch { data = {}; }
        if (res.ok) setOps(data); else setOps(null);
      } catch { setOps(null); }
    })();
  }, []);

  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap:2 }}>
      <Stack direction="row" spacing={1}>
        <Button size="small" variant={range==='day'?'contained':'outlined'} onClick={()=>setRange('day')}>일</Button>
        <Button size="small" variant={range==='week'?'contained':'outlined'} onClick={()=>setRange('week')}>주</Button>
        <Button size="small" variant={range==='month'?'contained':'outlined'} onClick={()=>setRange('month')}>월</Button>
      </Stack>
      {loading ? <CircularProgress size={22} /> : (
        <>
          <Box sx={{ display:'grid', gap:2, gridTemplateColumns:{ xs:'1fr', sm:'repeat(2,1fr)', md:'repeat(5,1fr)' } }}>
            <KpiCard title="총 발송" value={kpi.total} />
            <KpiCard title="성공" value={kpi.success} />
            <KpiCard title="실패" value={kpi.failed} />
            <KpiCard title="대기" value={kpi.pending} />
            <KpiCard title="성공률" value={`${kpi.successRate}%`} />
          </Box>
          <Box sx={{ display:'grid', gap:2, gridTemplateColumns:{ xs:'1fr', md:'repeat(2,1fr)' } }}>
            <SimpleBox title="유형/매체">
              <div style={{ width:'100%', height:260 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie dataKey="value" nameKey="name" cx="30%" cy="50%" outerRadius={70}
                         data={[{ name:'SMS', value:types.sms }, { name:'MMS', value:types.mms }]}>
                      <Cell fill="#4e79a7" /><Cell fill="#f28e2b" />
                    </Pie>
                    <Pie dataKey="value" nameKey="name" cx="75%" cy="50%" outerRadius={70}
                         data={[{ name:'단건', value:types.single }, { name:'대량', value:types.bulk }]}>
                      <Cell fill="#59a14f" /><Cell fill="#e15759" />
                    </Pie>
                    <Legend verticalAlign="bottom" height={24} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </SimpleBox>
            <SimpleBox title="운영 현황">
              <div style={{ color:'#555', fontSize:14 }}>
                {ops ? (
                  <>
                    오늘 발송 {ops.today?.total || 0}건 · 성공 {ops.today?.success || 0} · 실패 {ops.today?.failed || 0} · 대기 {ops.today?.pending || 0}<br/>
                    전체 대기 {ops.pendingAll || 0}건
                  </>
                ) : '운영 현황을 불러오는 중입니다.'}
              </div>
            </SimpleBox>
          </Box>
          <SimpleBox title="일별 발송량">
            <div style={{ width:'100%', height:260 }}>
              <ResponsiveContainer>
                <BarChart data={daily} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Bar dataKey="total" fill="#8884d8" name="총 발송" />
                  <Bar dataKey="success" fill="#82ca9d" name="성공" />
                  <Bar dataKey="failed" fill="#ff6b6b" name="실패" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </SimpleBox>
          <SimpleBox title="비용 추정">
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <TextField label="SMS 단가" size="small" type="number" value={unit.sms} onChange={(e)=>setUnit(u=>({ ...u, sms: Number(e.target.value||0) }))} sx={{ width: 120 }} />
              <TextField label="MMS 단가" size="small" type="number" value={unit.mms} onChange={(e)=>setUnit(u=>({ ...u, mms: Number(e.target.value||0) }))} sx={{ width: 120 }} />
            </Stack>
            <Typography>총 비용: {totalCost.toLocaleString()}원</Typography>
            <Typography>일평균: {avgCost.toLocaleString()}원</Typography>
          </SimpleBox>
          {stats?.errors && stats.errors.length > 0 && (
            <SimpleBox title="최근 에러(상위 10)">
              <ul style={{ margin:0, paddingLeft:18 }}>
                {stats.errors.map((e:any, i:number)=>(<li key={i}>{e.to} - {e.error}</li>))}
              </ul>
            </SimpleBox>
          )}
        </>
      )}
    </Box>
  );
};

const KpiCard: React.FC<{ title: string; value: any }> = ({ title, value }) => (
  <Box sx={{ p:1.5, border:'1px solid #eee', borderRadius:2, bgcolor:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}>
    <Typography sx={{ fontSize:12, color:'#777' }}>{title}</Typography>
    <Typography sx={{ fontSize:22, fontWeight:700 }}>{value}</Typography>
  </Box>
);

const SimpleBox: React.FC<{ title: string; children: any }> = ({ title, children }) => (
  <Box sx={{ p:1.5, border:'1px solid #eee', borderRadius:2, bgcolor:'#fff' }}>
    <Typography sx={{ fontWeight:700, mb:1 }}>{title}</Typography>
    {children}
  </Box>
);

const TemplateTab: React.FC = () => {
  const [companies, setCompanies] = useState<Array<{ id: string; name: string }>>([]);
  const [companyId, setCompanyId] = useState('');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Template>({ name: '', content: '', isDefault: false, media: [] });

  const fetchCompanies = async () => {
    setError(null);
    try {
      // Firestore의 dispatch_company 전체 목록을 로드 (템플릿 존재 여부와 무관)
      // 일부 문서에는 name 필드가 없고 문서 ID가 상호명인 구조가 있으므로 정렬 없이 전체 로드
      const snap = await getDocs(collection(db, 'dispatch_company'));
      const all: Array<{ id: string; name: string }> = [];
      snap.forEach(d => {
        const v = d.data() as any;
        all.push({ id: d.id, name: v?.company || v?.name || d.id });
      });
      setCompanies(all);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const fetchTemplates = async (cid: string) => {
    setError(null);
    if (!cid) { setTemplates([]); return; }
    setLoading(true);
    try {
      const res = await fetch(`/api/sms/templates?companyId=${encodeURIComponent(cid)}`);
      const raw = await res.text();
      let data: any = {};
      try { data = JSON.parse(raw); } catch { throw new Error(`HTTP ${res.status} ${res.statusText} - ${raw.slice(0,80)}`); }
      if (!res.ok || data?.success === false) throw new Error(data?.error || '템플릿 목록 실패');
      setTemplates(data.templates || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ name: '', content: '', isDefault: false, media: [] });
    setEditorOpen(true);
  };

  const openEdit = (t: Template) => {
    setEditingId(t.id || null);
    setForm({ name: t.name, content: t.content, isDefault: !!t.isDefault, media: (t.media || []).slice() });
    setEditorOpen(true);
  };

  const saveTemplate = async () => {
    if (!companyId || !form.name || !form.content) {
      setError('회사, 이름, 본문은 필수입니다.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (editingId) {
        const res = await fetch(`/api/sms/templates/${editingId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ companyId, ...form })
        });
        const raw = await res.text();
        let data: any = {}; try { data = JSON.parse(raw); } catch { throw new Error(`HTTP ${res.status} ${res.statusText} - ${raw.slice(0,80)}`); }
        if (!res.ok || data?.success === false) throw new Error(data?.error || '수정 실패');
      } else {
        const res = await fetch('/api/sms/templates', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ companyId, ...form })
        });
        const raw = await res.text();
        let data: any = {}; try { data = JSON.parse(raw); } catch { throw new Error(`HTTP ${res.status} ${res.statusText} - ${raw.slice(0,80)}`); }
        if (!res.ok || data?.success === false) throw new Error(data?.error || '생성 실패');
      }
      setEditorOpen(false);
      await fetchTemplates(companyId);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (id?: string) => {
    if (!id || !companyId) return;
    if (!window.confirm('템플릿을 삭제할까요?')) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/sms/templates/${id}?companyId=${encodeURIComponent(companyId)}`, { method: 'DELETE' });
      const raw = await res.text();
      let data: any = {}; try { data = JSON.parse(raw); } catch { throw new Error(`HTTP ${res.status} ${res.statusText} - ${raw.slice(0,80)}`); }
      if (!res.ok || data?.success === false) throw new Error(data?.error || '삭제 실패');
      await fetchTemplates(companyId);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { fetchCompanies(); }, []);
  React.useEffect(() => { if (companyId) fetchTemplates(companyId); }, [companyId]);

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          select size="small" label="거래처"
          value={companyId}
          onChange={(e)=> setCompanyId(e.target.value)}
          sx={{ minWidth: 260 }}
        >
          {companies.map(c => (
            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
          ))}
        </TextField>
        <Button variant="contained" disabled={!companyId} onClick={openCreate}>템플릿 추가</Button>
      </Stack>

      <Box sx={{ border: '1px solid #eee', borderRadius: 1, p: 1 }}>
        {loading ? (
          <Box sx={{ p: 2 }}><CircularProgress size={22} /></Box>
        ) : templates.length === 0 ? (
          <Typography sx={{ color: '#777', p: 1 }}>템플릿이 없습니다.</Typography>
        ) : (
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' } }}>
            {templates.map(t => (
              <Box key={t.id} sx={{ position: 'relative', p: 1.5, border: '1px solid #f0f0f0', borderRadius: 2, bgcolor: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.04)', minHeight: 160 }}>
                <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
                  <IconButton size="small" onClick={()=>openEdit(t)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={()=>deleteTemplate(t.id)}><DeleteIcon fontSize="small" /></IconButton>
                </Box>
                <Typography sx={{ fontWeight: 700, pr: 8 }}>{t.name}{t.isDefault ? ' (기본)' : ''}</Typography>
                <Typography variant="body2" sx={{ color: '#666', whiteSpace: 'pre-wrap', mt: .5 }}>
                  {t.content.slice(0, 140)}{t.content.length>140?'...':''}
                </Typography>
                {!!t.media && t.media.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    {t.media.filter(m=>m.type?.startsWith('image')).slice(0,4).map((m, idx) => (
                      <img
                        key={idx}
                        src={m.url}
                        alt={m.name || `img-${idx}`}
                        style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee', cursor: 'pointer' }}
                        onClick={() => { setPreviewSrc(m.url); setPreviewOpen(true); }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Dialog open={editorOpen} onClose={()=>setEditorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? '템플릿 수정' : '템플릿 추가'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="템플릿명" size="small" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} />
            <TextField label="본문" multiline minRows={8} value={form.content} onChange={(e)=>setForm({...form, content: e.target.value})} />
            <Box>
              <Typography variant="body2" sx={{ mb: 1, color: '#555' }}>이미지(선택)</Typography>
              <Button variant="outlined" component="label" size="small">이미지 업로드
                <input hidden type="file" accept="image/*" onChange={async (e)=>{
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    // 백엔드로 직접 업로드(멀티파트) → CORS 회피
                    const formData = new FormData();
                    formData.append('file', file);
                    const upRes = await fetch('/api/sms/templates/upload', { method: 'POST', body: formData });
                    const upRaw = await upRes.text();
                    let up: any = {}; try { up = JSON.parse(upRaw); } catch { throw new Error('업로드 응답 파싱 오류'); }
                    if (!upRes.ok || up?.success === false) throw new Error(up?.error || '업로드 실패');

                    const media: TemplateMedia[] = Array.isArray(form.media) ? form.media.slice() : [];
                    media.push({ type: 'image', url: up.publicUrl, name: up.name || file.name, size: up.size || file.size });
                    setForm({ ...form, media });
                  } catch (err: any) {
                    setError(err.message || '이미지 업로드 실패');
                  }
                }} />
              </Button>
              {!!form.media && form.media.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  {form.media.filter(m=>m.type?.startsWith('image')).map((m, idx) => (
                    <Box key={`${m.url}-${idx}`} sx={{ position: 'relative' }}>
                      <img
                        src={m.url}
                        alt={m.name || `img-${idx}`}
                        style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee', cursor: 'pointer' }}
                        onClick={() => { setPreviewSrc(m.url); setPreviewOpen(true); }}
                      />
                      <IconButton size="small" onClick={()=>{
                        const next = (form.media || []).slice();
                        next.splice(idx,1);
                        setForm({ ...form, media: next });
                      }} sx={{ position: 'absolute', top: -10, right: -10 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setEditorOpen(false)}>취소</Button>
          <Button onClick={saveTemplate} variant="contained" disabled={loading}>{loading ? <CircularProgress size={18} /> : '저장'}</Button>
        </DialogActions>
      </Dialog>

      {/* 이미지 미리보기 다이얼로그 */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>이미지 미리보기</DialogTitle>
        <DialogContent>
          {previewSrc && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <img src={previewSrc} alt="preview" style={{ maxWidth: '100%', maxHeight: '80vh' }} />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Stack>
  );
};


